import React, { useEffect, useRef } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './InkSound.css';

// Pentatonic C·D·E·G·A over two octaves (C4..A5), as MIDI numbers.
const SCALE_MIDI = [60, 62, 64, 67, 69, 72, 74, 76, 79, 81];
const midiToFreq = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

const GAIN_MAX = 0.25;
const GAIN_K = 0.09;      // speed(px/ms) → gain
const INK_WIDTH = 18;     // thick black trail
const FADE_ALPHA = 0.04;  // white veil per frame → slow decay
const MORPH_MS = 200;     // sine → square → sine on click
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

interface Ripple { x: number; y: number; r: number; }

const InkSound: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  // pointer
  const curRef = useRef({ x: 0, y: 0, has: false });
  const drawnRef = useRef({ x: 0, y: 0 });
  const ripplesRef = useRef<Ripple[]>([]);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });

  // audio (created lazily on first gesture to satisfy autoplay policy)
  const ctxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const clickTimeRef = useRef(-1);

  const exp = experiments.find(e => e.id === 'ink-sound');

  // Blended periodic wave: m=0 pure sine, m=1 → odd harmonics (square-ish).
  const morphWave = (ctx: AudioContext, m: number): PeriodicWave => {
    const N = 24;
    const real = new Float32Array(N);
    const imag = new Float32Array(N);
    for (let n = 1; n < N; n++) {
      if (n === 1) imag[n] = 1;                          // fundamental always
      else if (n % 2 === 1) imag[n] = (4 / (Math.PI * n)) * m; // odd harmonics grow with m
    }
    return ctx.createPeriodicWave(real, imag);
  };

  // First gesture: unlock audio + spawn the persistent oscillator.
  const ensureAudio = () => {
    if (ctxRef.current) {
      if (ctxRef.current.state === 'suspended') ctxRef.current.resume().catch(() => {});
      return;
    }
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = midiToFreq(SCALE_MIDI[0]);
    gain.gain.value = 0;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    ctxRef.current = ctx;
    oscRef.current = osc;
    gainRef.current = gain;
    ctx.resume().catch(() => {});
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // ── pointer (mouse + touch + pen via Pointer Events) ──
    const setCur = (e: PointerEvent) => {
      const c = curRef.current;
      if (!c.has) { drawnRef.current = { x: e.clientX, y: e.clientY }; }
      c.x = e.clientX; c.y = e.clientY; c.has = true;
    };
    const onMove = (e: PointerEvent) => { setCur(e); };
    const onDown = (e: PointerEvent) => {
      setCur(e);
      ensureAudio();
      ripplesRef.current.push({ x: e.clientX, y: e.clientY, r: 0 });
      clickTimeRef.current = performance.now();
    };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerdown', onDown);

    let prevT = performance.now();

    const tick = (now: number) => {
      const { w, h } = sizeRef.current;
      const dt = Math.max(1, now - prevT);
      prevT = now;

      // 1. fade veil — old ink slowly returns to white
      ctx.fillStyle = `rgba(255,255,255,${FADE_ALPHA})`;
      ctx.fillRect(0, 0, w, h);

      // 2. ink trail + instantaneous speed
      const cur = curRef.current;
      const drawn = drawnRef.current;
      let speed = 0;
      if (cur.has) {
        const dx = cur.x - drawn.x;
        const dy = cur.y - drawn.y;
        const dist = Math.hypot(dx, dy);
        speed = dist / dt; // px/ms
        if (dist > 0.01) {
          ctx.strokeStyle = '#000';
          ctx.lineWidth = INK_WIDTH;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(drawn.x, drawn.y);
          ctx.lineTo(cur.x, cur.y);
          ctx.stroke();
        }
        drawnRef.current = { x: cur.x, y: cur.y };
      }

      // 3. click ripples — expand past the canvas edge
      const maxR = Math.hypot(w, h) * 1.1;
      const rips = ripplesRef.current;
      for (let i = rips.length - 1; i >= 0; i--) {
        const rp = rips[i];
        rp.r += (maxR / 800) * dt; // ~0.8s to cross
        const f = rp.r / maxR;
        if (f >= 1) { rips.splice(i, 1); continue; }
        ctx.strokeStyle = `rgba(0,0,0,${(1 - f) * 0.5})`;
        ctx.lineWidth = clamp(8 * (1 - f), 0.5, 8);
        ctx.beginPath();
        ctx.arc(rp.x, rp.y, rp.r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // 4. audio — Y → pentatonic pitch, speed → gain, click → waveform morph
      const actx = ctxRef.current;
      const osc = oscRef.current;
      const gain = gainRef.current;
      if (actx && osc && gain) {
        const yNorm = clamp(cur.has ? cur.y / h : 1, 0, 1);
        const idx = clamp(Math.floor((1 - yNorm) * SCALE_MIDI.length), 0, SCALE_MIDI.length - 1);
        osc.frequency.setTargetAtTime(midiToFreq(SCALE_MIDI[idx]), actx.currentTime, 0.008);

        const ct = clickTimeRef.current;
        const since = ct >= 0 ? now - ct : Infinity;
        const clickEnv = since < MORPH_MS ? (1 - since / MORPH_MS) * 0.18 : 0;
        const speedGain = clamp(speed * GAIN_K, 0, GAIN_MAX);
        gain.gain.setTargetAtTime(Math.max(speedGain, clickEnv), actx.currentTime, 0.02);

        if (since < MORPH_MS) {
          const m = Math.sin(Math.PI * (since / MORPH_MS)); // 0→1→0
          osc.setPeriodicWave(morphWave(actx, m));
        } else if (ct >= 0 && osc.type !== 'sine') {
          osc.type = 'sine';
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerdown', onDown);
      oscRef.current?.stop();
      ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
    };
  }, []);

  return (
    <div className="inkSound">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />
      <canvas ref={canvasRef} className="inkCanvas" />
      <span className="inkHint">draw to play</span>
    </div>
  );
};

export default InkSound;
